"use client";

import { useEffect, type PropsWithChildren } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useLocale } from "next-intl";

import PageLoadingState from "@/components/page-loading-state";
import { useConnectorAccess } from "@/hooks/use-connector-access";
import { getLocaleRelativePath, isBareRoute } from "@/lib/bare-routes";
import { useWorkspaceStore } from "@/store/workspace/workspace-store";

export default function ConnectorAppAccessGate({
  children,
}: PropsWithChildren) {
  const pathname = usePathname();
  const locale = useLocale();
  const localeRelativePath = getLocaleRelativePath(pathname, locale);

  if (isBareRoute(localeRelativePath)) {
    return children;
  }

  return <WorkspaceProtectedGate>{children}</WorkspaceProtectedGate>;
}

function WorkspaceProtectedGate({ children }: PropsWithChildren) {
  const router = useRouter();
  const workspaces = useWorkspaceStore((state) => state.workspaces);
  const workspacesLoaded = useWorkspaceStore(
    (state) => state.workspacesLoaded,
  );
  const { canAccessApp, isLoading } = useConnectorAccess();
  const isForbidden =
    workspacesLoaded &&
    (workspaces.length === 0 || (!isLoading && !canAccessApp));

  useEffect(() => {
    if (isForbidden) {
      router.replace("/403");
    }
  }, [isForbidden, router]);

  if (!workspacesLoaded || isLoading || isForbidden) {
    return <PageLoadingState className="min-h-screen bg-white" />;
  }

  return children;
}
